'use client';

import React, { useRef, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useSWR from 'swr';

import { useUserInfo } from '@/context/UserInfoContext';
import { traceAction } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';
import type { PublicDocId } from '@/lib/api';
import {
  INVITE_DASHBOARD_URL,
  buildInviteUrl,
  cancelInvite,
  copyInviteUrl,
  createInvite,
  getInviteDashboard,
  requestInviteCredits,
  type PendingInvite,
} from '@/lib/invites';

import { InviteForm, InviteSuccess, type InviteFormValues } from './InviteForm';

export interface InviteManagerProps {
  threadContext?: PublicDocId;
  showQrCode?: boolean;
  onDone?: () => void;
}

type Mutation = 'create' | 'cancel' | 'request' | null;

/** Owns invitation state and every mutation for both page and modal surfaces. */
export const InviteManager: React.FC<InviteManagerProps> = ({
  threadContext,
  showQrCode = false,
  onDone,
}) => {
  const { userInfo, reloadUserInfo } = useUserInfo();
  const {
    data: dashboard,
    error: dashboardError,
    isLoading,
    mutate,
  } = useSWR(INVITE_DASHBOARD_URL, getInviteDashboard, {
    revalidateOnFocus: true,
    onSuccess: () => void reloadUserInfo(),
  });
  const mutationLocked = useRef(false);
  const [activeMutation, setActiveMutation] = useState<Mutation>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [createdInvite, setCreatedInvite] = useState<PendingInvite | null>(null);

  const beginMutation = (mutation: Exclude<Mutation, null>): boolean => {
    if (mutationLocked.current) return false;
    mutationLocked.current = true;
    setActiveMutation(mutation);
    setOperationError(null);
    return true;
  };

  const finishMutation = () => {
    mutationLocked.current = false;
    setActiveMutation(null);
  };

  const refreshDashboard = async () => {
    try {
      await mutate();
    } catch (error) {
      console.error('Error refreshing invitations:', error);
      setOperationError('Failed to refresh invitations.');
    }
  };

  const handleCreate = async (values: InviteFormValues) => {
    if (!beginMutation('create')) return;
    try {
      const response = await traceAction(FEEDBACK_ACTION.CREATE_INVITE, () =>
        createInvite({
          inviter_topics: values.inviterTopics.map((topic) => topic.cref),
          invitee_topics: values.inviteeTopics.map((topic) => topic.cref),
          ...(values.nickname ? { nickname: values.nickname } : {}),
          ...(threadContext ? { thread_context: threadContext } : {}),
        })
      );
      if (response.status === 'success') {
        setCreatedInvite(response.invite);
      } else if (response.status === 'no_credits') {
        setOperationError('You have no invitation credits left.');
      } else if (response.status === 'email_verification_required') {
        setOperationError('Verify your email before creating an invitation.');
        await reloadUserInfo();
      }
    } catch (error) {
      console.error('Error creating invitation:', error);
      setOperationError('Failed to create the invitation. Please try again.');
      finishMutation();
      return;
    }
    await refreshDashboard();
    finishMutation();
  };

  const handleCancel = async (inviteId: number) => {
    if (!window.confirm('Cancel this invitation? The link will stop working.')) return;
    if (!beginMutation('cancel')) return;
    try {
      await cancelInvite(inviteId);
      if (createdInvite?.id === inviteId) setCreatedInvite(null);
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setOperationError('Failed to cancel the invitation.');
      finishMutation();
      return;
    }
    await refreshDashboard();
    finishMutation();
  };

  const handleRequest = async () => {
    if (!beginMutation('request')) return;
    try {
      await requestInviteCredits();
    } catch (error) {
      console.error('Error requesting invitation credits:', error);
      setOperationError('Failed to request more invitation credits.');
      finishMutation();
      return;
    }
    await refreshDashboard();
    finishMutation();
  };

  const handleCopy = async (invite: PendingInvite) => {
    try {
      await copyInviteUrl(invite.code);
    } catch {
      setOperationError('Failed to copy the invitation link. Please copy it manually.');
    }
  };

  if (isLoading || !userInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress aria-label="Loading invitations" />
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Alert severity="error" action={<Button onClick={() => void mutate()}>Retry</Button>}>
        Failed to load invitations.
      </Alert>
    );
  }

  const emailVerified = userInfo.email.length > 0;
  const isAdmin = userInfo.is_admin;

  return (
    <Stack spacing={3}>
      <Typography variant="h6" component="p">
        {isAdmin
          ? 'Administrator invitations do not use credits.'
          : `Invitation credits: ${dashboard.credits_remaining}`}
      </Typography>

      {!emailVerified && (
        <Alert severity="info">
          Verify your email in <Link href="/account">account settings</Link> before creating a new
          invitation. You can still manage invitations you already created.
        </Alert>
      )}

      {createdInvite ? (
        <InviteSuccess
          invite={createdInvite}
          showQrCode={showQrCode}
          onCopyError={() => setOperationError('Failed to copy the invitation link.')}
          onDone={() => {
            if (onDone) onDone();
            else setCreatedInvite(null);
          }}
        />
      ) : emailVerified && (isAdmin || dashboard.credits_remaining > 0) ? (
        <InviteForm
          introMessage={showQrCode ? null : undefined}
          disabled={activeMutation !== null}
          onSubmit={(values) => void handleCreate(values)}
          onCancel={onDone}
        />
      ) : !isAdmin && dashboard.credits_remaining === 0 ? (
        dashboard.credits_requested_at ? (
          <Alert severity="info">
            Your request for more invitation credits is pending administrator review.
          </Alert>
        ) : (
          <Box>
            <Typography component="p" sx={{ mb: 1 }}>
              You have used your current invitation credits.
            </Typography>
            <Button
              variant="contained"
              onClick={() => void handleRequest()}
              disabled={activeMutation !== null}
            >
              {activeMutation === 'request' ? 'Requesting...' : 'Request More'}
            </Button>
          </Box>
        )
      ) : null}

      {dashboard.pending_invites.length > 0 && (
        <Box component="section" aria-labelledby="pending-invitations-heading">
          <Typography id="pending-invitations-heading" variant="h6" component="h2">
            Pending invitations
          </Typography>
          <List>
            {dashboard.pending_invites.map((invite) => {
              const inviteUrl = buildInviteUrl(invite.code);
              return (
                <ListItem
                  key={invite.id}
                  divider
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        aria-label={`Copy invitation for ${invite.invitee_nickname || 'friend'}`}
                        onClick={() => void handleCopy(invite)}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <Button
                        color="error"
                        aria-label={`Cancel invitation for ${invite.invitee_nickname || 'friend'}`}
                        onClick={() => void handleCancel(invite.id)}
                        disabled={activeMutation !== null}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={invite.invitee_nickname || 'Friend'}
                    secondary={
                      <Stack component="span" spacing={0.5}>
                        <span>{new Date(invite.created_at).toLocaleString()}</span>
                        <span>
                          Invite code: <code>{invite.code}</code>
                        </span>
                        <Link
                          href={inviteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ overflowWrap: 'anywhere' }}
                        >
                          {inviteUrl}
                        </Link>
                      </Stack>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {(operationError || dashboardError) && (
        <Alert severity="error">{operationError ?? 'Failed to refresh invitations.'}</Alert>
      )}
    </Stack>
  );
};
